import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useApp';
import { ApiError, api } from '@/lib/api';
import { Button, Field, Input, Spinner } from '@/components/ui';
import type { AuthResponse } from '@/lib/types';

function OAuthButtons({ returnUrl }: { returnUrl: string }) {
  // A full navigation, not fetch: the OAuth flow is a series of cross-site redirects that the
  // browser has to follow itself, and XHR cannot leave the origin and come back with a cookie.
  const start = (provider: string) => {
    window.location.href = `/api/auth/${provider}?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  return (
    <div className="grid gap-2">
      <Button type="button" variant="secondary" size="lg" onClick={() => start('google')}>
        Nastavi sa Google nalogom
      </Button>
      <Button type="button" variant="secondary" size="lg" onClick={() => start('facebook')}>
        Nastavi sa Facebook nalogom
      </Button>
    </div>
  );
}

function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative overflow-hidden">
      <div className="aurora">
        <span />
      </div>

      <div className="relative mx-auto flex max-w-md flex-col px-4 py-12 sm:py-16">
        <div className="animate-fade-in-up mb-8 text-center">
          <span className="relative mx-auto mb-5 flex size-14 items-center justify-center overflow-hidden rounded-[18px] bg-accent text-on-accent shadow-[inset_0_1px_0_0_rgb(255_255_255/0.25),0_12px_30px_-12px_var(--accent)]">
            <span className="font-display text-2xl font-extrabold">K</span>
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-y-1/2 -left-1/3 w-2/3 -rotate-[28deg] bg-white/25 blur-[2px]"
            />
          </span>
          <h1 className="text-2xl font-bold text-body">{title}</h1>
          <p className="mt-1.5 text-sm text-muted">{subtitle}</p>
        </div>

        <div className="animate-fade-in-up rounded-[var(--radius-panel)] border border-default bg-raised p-6 shadow-card">
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const returnUrl = (location.state as { from?: string } | null)?.from ?? '/';

  useEffect(() => {
    if (user) {
      navigate(returnUrl, { replace: true });
    }
  }, [user, navigate, returnUrl]);

  useEffect(() => {
    // The OAuth callback redirects here with a reason when it could not complete, so the visitor
    // is told what happened instead of silently landing back on an empty form.
    const reason = searchParams.get('greska');
    if (reason === 'email') {
      setError('Nalog nema e-mail adresu. Prijavite se lozinkom ili dodajte e-mail kod provajdera.');
    } else if (reason === 'oauth') {
      setError('Prijava preko društvene mreže nije uspela. Pokušajte ponovo.');
    }
  }, [searchParams]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await login(email, password);
      navigate(returnUrl, { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Prijava nije uspela.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Prijava" subtitle="Dobrodošli nazad">
      <form onSubmit={submit} className="space-y-4">
        <Field label="E-mail" required>
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label="Lozinka" required>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        {error && (
          <p role="alert" className="animate-fade-in-down rounded-xl border border-danger/20 bg-danger/10 p-3 text-sm text-danger">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Prijavi se
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-faint">
        <span className="h-px flex-1 bg-[var(--border)]" />
        ili
        <span className="h-px flex-1 bg-[var(--border)]" />
      </div>

      <OAuthButtons returnUrl={returnUrl} />

      <p className="mt-6 text-center text-sm text-muted">
        Nemate nalog?{' '}
        <Link to="/registracija" className="font-medium text-accent hover:underline">
          Registrujte se
        </Link>
      </p>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: '', password: '', displayName: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await register(form.email, form.password, form.displayName);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Registracija nije uspela.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Registracija" subtitle="Besplatno, bez reklama">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Ime" required>
          <Input
            autoComplete="name"
            required
            value={form.displayName}
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
          />
        </Field>

        <Field label="E-mail" required>
          <Input
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
        </Field>

        <Field label="Lozinka" hint="Najmanje 10 karaktera" required>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
          />
        </Field>

        {error && (
          <p role="alert" className="animate-fade-in-down rounded-xl border border-danger/20 bg-danger/10 p-3 text-sm text-danger">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Napravi nalog
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-faint">
        <span className="h-px flex-1 bg-[var(--border)]" />
        ili
        <span className="h-px flex-1 bg-[var(--border)]" />
      </div>

      <OAuthButtons returnUrl="/" />

      <p className="mt-6 text-center text-sm text-muted">
        Već imate nalog?{' '}
        <Link to="/prijava" className="font-medium text-accent hover:underline">
          Prijavite se
        </Link>
      </p>
    </AuthShell>
  );
}

/**
 * Where the OAuth redirect lands.
 *
 * The server puts the access token in the URL fragment, which browsers never send to servers and
 * never write into referrer headers. It is read once, exchanged for an in-memory session, and the
 * fragment is stripped from history so it cannot be recovered from the back button.
 */
export function AuthCallbackPage() {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const accessToken = fragment.get('access_token');
    const next = searchParams.get('next') ?? '/';

    if (!accessToken) {
      navigate('/prijava?greska=oauth', { replace: true });
      return;
    }

    window.history.replaceState(null, '', window.location.pathname);

    (async () => {
      try {
        // The refresh cookie is already set, so this both proves the session works and yields the
        // user record without needing the server to encode it into the redirect.
        const auth = await api<AuthResponse>('/api/auth/refresh', { method: 'POST', body: {} });
        adopt(auth);
        navigate(next, { replace: true });
      } catch {
        navigate('/prijava?greska=oauth', { replace: true });
      }
    })();
  }, [adopt, navigate, searchParams]);

  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24">
      <Spinner className="size-8 text-accent" />
      <p className="text-sm text-muted">Prijavljivanje…</p>
    </div>
  );
}
